/**
 * 로그인 페이지
 * 
 * @description
 * 사용자가 로그인 ID와 비밀번호를 입력하여 로그인하는 페이지
 * 
 * @features
 * - 로그인 ID, 비밀번호 입력
 * - 로그인 성공 시 accessToken, refreshToken LocalStorage 저장
 * - 로그인 실패 시 AlertDialog 표시
 * - 회원가입/비밀번호 찾기 링크
 * 
 * @api
 * - POST /user/login
 * - GET /user/profile
 * 
 * @routes
 * - /login (현재 페이지)
 * - /signup (회원가입)
 * - /password-reset (비밀번호 찾기)
 */

import React, { useState, useCallback } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { getSafeRedirectPath } from '../../utils/redirectPath';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Input, AlertDialog } from '@/components/shared/ui';
import { buttonVariants } from '@/components/shared/ui/Button';
import { cn } from '@/lib/cn';
import { ROUTES } from '@/lib/constants/routes';
import { useUser } from '@/data/queries/useUser';
import { convertRoleNumberToString, resolveLicenseFromProfile } from '@/utils/roleUtils';
import type { LoginDto } from '@/types/user';
import { confirmJoinClass } from '../../services/classService';

const medicrewLogo = '/assets/images/brand/medicrew_blue_logo.png';

/** 
 * 로그인 ID: 영문 소문자, 숫자, 특수기호만 허용
 * 한글(자모/완성형), 대문자 영문 제거
 */
function sanitizeLoginId(value: string): string {
	// 한글 제거
	let sanitized = value.replace(/[ㄱ-ㅎㅏ-ㅣ가-힣]/g, '');
	// 대문자를 소문자로 변환
	sanitized = sanitized.toLowerCase();
	return sanitized;
}

/**
 * 로그인 ID 입력 검증
 * @returns true: 유효하지 않은 문자 포함(한글), false: 모든 문자 유효
 */
function hasInvalidLoginIdChars(value: string): boolean {
	// 한글이 포함되어 있는지 확인 (대문자는 자동 변환되므로 체크하지 않음)
	return /[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(value);
}

export default function Login(): React.ReactElement {
	const { t } = useTranslation(['auth']);
	// ========== API Hooks ==========
	const { login: loginApi, getProfile } = useUser();

	// ========== State 관리 ==========

	/**
	 * 로그인 폼 데이터
	 * @field loginId - 로그인 ID
	 * @field password - 비밀번호
	 */
	const [formData, setFormData] = useState<LoginDto>({
		loginId: '',
		password: '',
	});

	/**
	 * 로그인 진행 중 상태
	 * @description 로그인 API 호출 중 버튼 비활성화
	 */
	const [isLoading, setIsLoading] = useState<boolean>(false);

	/**
	 * AlertDialog 상태
	 * @field isOpen - 팝업 열림 여부
	 * @field message - 표시할 메시지
	 */
	const [alertState, setAlertState] = useState<{ isOpen: boolean; message: string; }>({
		isOpen: false,
		message: '',
	});

	/**
	 * 로그인 ID 입력 에러 상태
	 * @description 유효하지 않은 문자(한글) 입력 시 에러 메시지 표시
	 */
	const [loginIdError, setLoginIdError] = useState<string>('');

	// ========== Hooks ==========

	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const { login } = useAuth();
	/** 로그인 전 접근 시도 페이지 (RequireRole 등에서 state.from으로 전달, state 포함 시 주문확인 등 복원) */
	const from = (location.state as { from?: { pathname: string; search?: string; hash?: string; state?: unknown } })?.from;

	// ========== Helper 함수 ==========

	/**
	 * AlertDialog 표시 함수
	 * @param message - 표시할 메시지
	 */
	const showAlert = useCallback((message: string) => {
		setAlertState({ isOpen: true, message });
	}, []);

	/**
	 * AlertDialog 닫기 함수
	 */
	const closeAlert = useCallback(() => {
		setAlertState({ isOpen: false, message: '' });
	}, []);

	// ========== 로그인 처리 ==========

	/**
	 * 로그인 폼 제출 핸들러
	 * 
	 * @description
	 * 로그인 ID와 비밀번호를 검증하고 로그인 API 호출
	 * 
	 * @param e - 폼 제출 이벤트
	 * 
	 * @validation
	 * 1. 로그인 ID 입력 여부 확인 → "로그인 ID를 입력해주세요."
	 * 2. 비밀번호 입력 여부 확인 → "비밀번호를 입력해주세요."
	 * 
	 * @api
	 * - POST /user/login
	 * - 토큰 저장 및 페이지 이동
	 */
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// ========== 1단계: 유효성 검사 ==========

		// 로그인 ID 입력 여부 확인
		if (!formData.loginId) {
			showAlert(t('auth:alertLoginIdRequired'));
			return;
		}

		// 비밀번호 입력 여부 확인
		if (!formData.password) {
			showAlert(t('auth:alertPasswordRequired'));
			return;
		}

		// ========== 2단계: 실제 API 로그인 ==========

		setIsLoading(true);
		setLoginIdError(''); // 로그인 시도 시 에러 상태 초기화

		try {
			const { accessToken, refreshToken } = await loginApi(formData);

			// 토큰 저장 (apiClient와 동일한 키 사용) — access 필수, refresh는 스펙에 있을 때만
			localStorage.setItem('medicrew_access_token', accessToken);
			if (refreshToken?.trim()) {
				localStorage.setItem('medicrew_refresh_token', refreshToken.trim());
			} else {
				localStorage.removeItem('medicrew_refresh_token');
			}

		// ========== 실제 프로필 조회하여 AuthContext 업데이트 ==========
		const userProfile = await getProfile();

		// ========== role 숫자를 문자열로 변환 ==========
		// convertRoleNumberToString: 0 guest, 1 student, 2 master, 4 sales_admin→admin, 5 admin
		const userRole = convertRoleNumberToString(userProfile.role);
			
			const userLicense = resolveLicenseFromProfile(userProfile);

			// 기관 정보는 백엔드 프로필에서 내려준 값만 사용 (프로필/헤더 표시와 블로그 작성 등 권한 판단 일치)
			const organizationId = userProfile.organizationId != null ? String(userProfile.organizationId) : null;
			const organizationName = userProfile.organizationName?.trim() ?? '';

			// AuthContext 로그인 (실제 API 데이터 사용)
			const displayName = (userProfile.realName && userProfile.realName.trim()) ? userProfile.realName.trim() : formData.loginId;
			const currentAccountEntry = {
				accountId: `acc_${userProfile.userId}`,
				loginId: formData.loginId,
				displayName,
				email: userProfile.email || '',
				profileImageUrl: userProfile.profileImageUrl,
				phoneNumber: userProfile.phoneNumber?.trim() ?? '',
				affiliation: userProfile.affiliation?.trim() ?? '',
				position: userProfile.position?.trim() ?? '',
				department: userProfile.department?.trim() ?? '',
				organizationId,
				organizationName,
				role: userRole,
				license: userLicense,
				isActive: true
			};
			// 이전에 '다른 계정 추가'로 연결해 둔 계정 목록 유지 (같은 브라우저에서 재로그인 시)
			let accounts: typeof currentAccountEntry[] = [currentAccountEntry];
			try {
				let savedAccounts: typeof currentAccountEntry[] | null = null;
				const savedUser = localStorage.getItem('medicrew_user');
				if (savedUser) {
					const parsed = JSON.parse(savedUser);
					if (parsed?.accounts && Array.isArray(parsed.accounts) && parsed.accounts.length > 0) {
						savedAccounts = parsed.accounts as typeof currentAccountEntry[];
					}
				}
				if (savedAccounts) {
					const others = savedAccounts.filter(
						(acc: { loginId?: string }) => acc.loginId !== formData.loginId
					);
					accounts = [
						currentAccountEntry,
						...others.map((acc) => ({
							...acc,
							isActive: false
						}))
					];
				}
			} catch {
				// localStorage 파싱 실패 시 현재 계정만 사용
			}
			const guestAccountExpiresAt =
				userRole === 'guest' ? (userProfile.guestAccountExpiresAt ?? null) : null;

		const userData = {
			userId: userProfile.userId,  // User 테이블 PK (API 호출용)
			name: displayName,
			email: userProfile.email || '',
			profileImageUrl: userProfile.profileImageUrl,
			guestAccountExpiresAt,
			isAuthenticated: true,
			currentAccount: {
					accountId: currentAccountEntry.accountId,
					loginId: currentAccountEntry.loginId,
					displayName: currentAccountEntry.displayName,
					email: currentAccountEntry.email,
					profileImageUrl: currentAccountEntry.profileImageUrl,
					phoneNumber: currentAccountEntry.phoneNumber,
					affiliation: currentAccountEntry.affiliation,
					position: currentAccountEntry.position,
					department: currentAccountEntry.department,
					organizationId: currentAccountEntry.organizationId,
					organizationName: currentAccountEntry.organizationName,
					role: currentAccountEntry.role,
					license: currentAccountEntry.license
				},
				accounts
			};
			login(userData);

			// 보류 중인 초대 확인
			const pendingToken = localStorage.getItem('pending_invite_token');
			if (pendingToken) {
				localStorage.removeItem('pending_invite_token');
				navigate(`/class/invite/${pendingToken}`);
				return;
			}

			const pendingJoinClassId = localStorage.getItem('pending_join_class_id');
			if (pendingJoinClassId) {
				localStorage.removeItem('pending_join_class_id');
				const cid = Number(pendingJoinClassId);
				if (Number.isFinite(cid) && cid > 0) {
					try {
						await confirmJoinClass(cid);
					} catch {
						// ignore
					}
					navigate(`/student/my-classes/${cid}`);
					return;
				}
			}

			// 이전 화면(접근 시도했던 페이지)으로 이동
			if (from?.pathname) {
				navigate(from.pathname + (from.search ?? '') + (from.hash ?? ''), { replace: true, state: from.state });
				return;
			}

			// 세션 만료 등으로 ?redirect= 가 있으면 직전 화면으로 이동
			const redirectPath = getSafeRedirectPath(searchParams.get('redirect'));
			if (redirectPath) {
				navigate(redirectPath, { replace: true });
				return;
			}

			// 로그인 후 랜딩페이지로 이동
			navigate(ROUTES.HOME);
		} catch (error: any) {
			showAlert(error.message || t('auth:alertLoginError'));
		} finally {
			// 항상 실행: 로딩 상태 종료
			setIsLoading(false);
		}
	};

	return (
		<div className="h-dvh max-h-dvh flex flex-col overflow-hidden bg-gray-50 box-border">
			<div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain flex flex-col items-center justify-center w-full px-4 py-3 sm:px-6 sm:py-4">
			{/* Login Card */}
			<div className="w-full max-w-[460px] bg-white rounded-3xl p-14 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)] border border-gray-100">
				{/* Header: Logo & Title */}
				<div className="text-center mb-12">
					<Link to={ROUTES.HOME} className="inline-block mb-8">
						<img src={medicrewLogo} alt="Medicrew" className="h-7 w-auto" />
					</Link>
					<h1 className="text-2xl font-semibold text-brand-500 mb-2">
						{t('auth:login')}
				</h1>
				<p className="text-gray-500 text-base">
					{t('auth:welcomeMessage')}
				</p>
			</div>

				{/* Form */}
				<form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
					<div>
						<label htmlFor="loginId" className="block mb-2 text-sm font-semibold text-gray-900">
							{t('auth:idLabel')}
						</label>
						<Input
							type="text"
							id="loginId"
							className={`bg-gray-50 focus:bg-white border-transparent focus:border-blue-500 rounded-xl ${
								loginIdError ? 'border-red-500 focus:border-red-500' : ''
							}`}
							placeholder={t('auth:loginIdPlaceholder')}
							value={formData.loginId}
						onChange={(e) => {
							const inputValue = e.target.value;
							const hasInvalidChars = hasInvalidLoginIdChars(inputValue);
							
							// 한글 입력 시 에러 메시지 표시 (대문자는 자동 변환되므로 에러 표시 안 함)
							if (hasInvalidChars) {
								setLoginIdError('영문 소문자, 숫자, 특수기호만 입력가능합니다.');
							} else {
								setLoginIdError('');
							}
							
							// 입력값 정제 (한글 제거, 대문자→소문자 변환)
							setFormData((prev) => ({
								...prev,
								loginId: sanitizeLoginId(inputValue),
							}));
						}}
							required
						/>
						{loginIdError && (
							<p className="mt-1 text-sm text-red-500">{loginIdError}</p>
						)}
					</div>

					<div>
						<div className="flex justify-between mb-2">
							<label htmlFor="password" className="text-sm font-semibold text-gray-900">
								{t('auth:passwordLabel')}
							</label>
						</div>
						<Input
							type="password"
							id="password"
							className="bg-gray-50 focus:bg-white border-transparent focus:border-blue-500 rounded-xl"
							placeholder={t('auth:passwordPlaceholder')}
							value={formData.password}
							onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
							required
						/>
					</div>

					<Button
						type="submit"
						className="w-full mt-4 rounded-full text-lg h-12"
						variant="primary"
						disabled={isLoading}
					>
						{isLoading ? t('auth:loggingIn') : t('auth:login')}
					</Button>
				</form>

				{/* Links */}
				<div className="mt-8 flex justify-center gap-6 text-sm">
					<Link to={ROUTES.AUTH.SIGNUP} className="text-gray-500 hover:text-gray-900 transition-colors">
						{t('auth:signup')}
					</Link>
					<span className="text-gray-200">|</span>
					<Link to={ROUTES.AUTH.PASSWORD_RESET} className="text-gray-500 hover:text-gray-900 transition-colors">
						{t('auth:findPassword')}
					</Link>
				</div>

			</div>

			{/* 홈 / 약관 링크 (Powered by 위) — 홈 왼쪽, 약관·개인정보 오른쪽 */}
			<div className="w-full max-w-[460px] flex flex-row justify-between items-center gap-3 mt-10">
				<Link
					to={ROUTES.HOME}
					className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'text-xs sm:text-sm h-auto min-h-[32px] py-2 px-3 shrink-0')}
				>
					← {t('auth:homeLink')}
				</Link>
				<div className="flex flex-wrap justify-end items-center gap-1 sm:gap-2 min-w-0">
					<Link to={ROUTES.COMMON.TERMS} className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'text-xs sm:text-sm h-auto min-h-[32px] py-2 px-3')}>
						{t('auth:terms')}
					</Link>
					<Link to={ROUTES.COMMON.PRIVACY} className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'text-xs sm:text-sm h-auto min-h-[32px] py-2 px-3')}>
						{t('auth:privacy')}
					</Link>
				</div>
			</div>

			{/* Powered By */}
			<div className="mt-6 flex justify-center items-center gap-2">
				<span className="text-xs text-gray-400">Powered by</span>
				<span className="text-sm font-bold text-gray-500">NEWBASE</span>
			</div>

			{/* AlertDialog: 로그인 실패/오류 메시지 표시 */}
			<AlertDialog
				isOpen={alertState.isOpen}
				onClose={closeAlert}
				message={alertState.message}
			/>
			</div>
		</div>
	);
}
